<template>
  <section class="dashboard-page" :dir="pageDirection">
    <div class="section-head">
      <h2>{{ t("nationalScope.title") }}</h2>
      <p>{{ t("nationalScope.description") }}</p>
    </div>

    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />
        <h3>{{ t("nationalScope.loadingTitle") }}</h3>
        <p>{{ t("nationalScope.loadingDescription") }}</p>
      </div>
    </div>

    <template v-else>
      <div v-if="errorMessage" class="error-box">{{ errorMessage }}</div>

      <template v-else>
        <div class="kpi-grid">
          <div class="kpi-card blue">
            <div class="kpi-title">
              {{ t("nationalScope.kpis.governorates.title") }}
            </div>
            <div class="kpi-value">{{ totalGovernorates }}</div>
            <div class="kpi-note">
              {{ t("nationalScope.kpis.governorates.note") }}
            </div>
          </div>

          <div class="kpi-card blue">
            <div class="kpi-title">
              {{ t("nationalScope.kpis.districts.title") }}
            </div>
            <div class="kpi-value">{{ totalDistricts }}</div>
            <div class="kpi-note">
              {{ t("nationalScope.kpis.districts.note") }}
            </div>
          </div>

          <div class="kpi-card green">
            <div class="kpi-title">
              {{ t("nationalScope.kpis.evaluated.title") }}
            </div>
            <div class="kpi-value">{{ evaluatedDistrictsCount }}</div>
            <div class="kpi-note">
              {{ t("nationalScope.kpis.evaluated.note") }}
            </div>
          </div>

          <div class="kpi-card orange">
            <div class="kpi-title">
              {{ t("nationalScope.kpis.pending.title") }}
            </div>
            <div class="kpi-value">{{ pendingDistrictsCount }}</div>
            <div class="kpi-note">
              {{ t("nationalScope.kpis.pending.note") }}
            </div>
          </div>
        </div>

        <div class="filter-card">
          <v-select
            v-model="filters.governorate"
            :label="t('nationalScope.filters.governorate')"
            :items="governorates"
            item-title="name"
            item-value="key"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-select
            v-model="filters.status"
            :label="t('nationalScope.filters.status')"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-btn class="reset-btn" height="40" @click="resetFilters">
            {{ t("nationalScope.filters.reset") }}
          </v-btn>
        </div>

        <div class="table-card">
          <h3>{{ t("nationalScope.table.title") }}</h3>

          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{{ t("nationalScope.table.governorate") }}</th>
                  <th>{{ t("nationalScope.table.districts") }}</th>
                  <th>{{ t("nationalScope.table.evaluated") }}</th>
                  <th>{{ t("nationalScope.table.pending") }}</th>
                  <th>{{ t("nationalScope.table.facilities") }}</th>
                  <th>{{ t("nationalScope.table.status") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in filteredRows" :key="item.key">
                  <td
                    class="governorate-name"
                    :data-label="t('nationalScope.table.governorate')"
                  >
                    {{ item.name }}
                  </td>

                  <td :data-label="t('nationalScope.table.districts')">
                    {{ item.districts }}
                  </td>

                  <td :data-label="t('nationalScope.table.evaluated')">
                    {{ item.evaluated }}
                  </td>

                  <td :data-label="t('nationalScope.table.pending')">
                    {{ item.pending }}
                  </td>

                  <td :data-label="t('nationalScope.table.facilities')">
                    {{ item.facilities }}
                  </td>

                  <td :data-label="t('nationalScope.table.status')">
                    <span class="status" :class="item.statusType">
                      {{ item.status }}
                    </span>
                  </td>
                </tr>

                <tr v-if="filteredRows.length === 0" class="empty-card-row">
                  <td colspan="6" class="empty-row">
                    {{ t("nationalScope.empty") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const { t, locale } = useI18n();
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const loading = ref(false);
const errorMessage = ref("");

const governorates = ref([]);
const districts = ref([]);
const facilities = ref([]);

const filters = reactive({
  governorate: null,
  status: null,
});

const statusOptions = computed(() => [
  { title: t("nationalScope.statusOptions.partial"), value: "partial" },
  { title: t("nationalScope.statusOptions.pending"), value: "pending" },
]);

const normalize = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeName = (value) =>
  normalize(value)
    .replace(/\s+/g, " ")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .toLowerCase();

const unwrap = (response) => {
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data?.results)) return response.data.results;
  return [];
};

const getGovernorateName = (item) =>
  normalize(
    locale.value === "ar"
      ? item.governorate_name_ar ||
          item.governorate ||
          item.name_ar ||
          item.name ||
          item.governorate_name_en
      : item.governorate_name_en ||
          item.name_en ||
          item.governorate_name_ar ||
          item.governorate ||
          item.name
  );

const getDistrictName = (item) =>
  normalize(
    locale.value === "ar"
      ? item.district_name_ar ||
          item.district ||
          item.name_ar ||
          item.name ||
          item.district_name_en
      : item.district_name_en ||
          item.name_en ||
          item.district_name_ar ||
          item.district ||
          item.name
  );

const loadGovernorates = async () => {
  const response = await api.get("/ref-governorates");
  const data = unwrap(response);

  governorates.value = data.map((item, index) => {
    const code = normalize(
      item.governorate_code || item.province_code || item.code || item.id
    );

    const name =
      getGovernorateName(item) ||
      `${t("nationalScope.fallback.governorate")} #${index + 1}`;

    return {
      id: normalize(item.id || index + 1),
      code,
      key: code || normalizeName(name),
      name,
      matchKeys: [
        code,
        normalize(item.id),
        normalizeName(name),
        normalizeName(item.governorate_name_en),
      ].filter(Boolean),
    };
  });
};

const loadDistricts = async () => {
  const response = await api.get("/ref-districts");
  const data = unwrap(response);

  districts.value = data.map((item, index) => {
    const code = normalize(item.district_code || item.code || item.id);
    const name =
      getDistrictName(item) ||
      `${t("nationalScope.fallback.district")} #${index + 1}`;

    const governorateKey = normalize(
      item.governorate_code ||
        item.province_code ||
        item.ref_governorate_code ||
        item.governorate?.governorate_code ||
        item.governorate?.province_code ||
        item.governorate_id ||
        item.province_id ||
        item.ref_governorate_id
    );

    const governorateName = normalizeName(
      item.governorate_name_ar ||
        item.governorate ||
        item.governorate?.name_ar ||
        item.governorate?.name ||
        item.governorate_name_en
    );

    return {
      id: normalize(item.id || index + 1),
      code,
      key: code || normalizeName(name),
      governorateKey,
      governorateName,
      name,
    };
  });
};

const loadFacilities = async () => {
  const response = await api.get("/health-facilities");
  const data = unwrap(response);

  facilities.value = data.map((item, index) => {
    const governorateKey = normalize(
      item.province_code ||
        item.governorate_code ||
        item.ref_governorate_code ||
        item.governorate?.governorate_code ||
        item.governorate_id ||
        item.province_id ||
        item.ref_governorate_id
    );

    const districtKey = normalize(
      item.district_code ||
        item.ref_district_code ||
        item.district?.district_code ||
        item.district_id ||
        item.ref_district_id
    );

    const governorateName = normalizeName(
      item.governorate_name_ar ||
        item.governorate ||
        item.governorate?.name_ar ||
        item.governorate?.name
    );

    const districtName = normalizeName(
      item.district_name_ar ||
        item.district ||
        item.district?.name_ar ||
        item.district?.name
    );

    return {
      id: item.id || index + 1,
      governorateKey,
      districtKey,
      governorateName,
      districtName,
    };
  });
};

const governorateHasDistrict = (gov, district) => {
  return (
    gov.matchKeys.includes(normalize(district.governorateKey)) ||
    gov.matchKeys.includes(normalizeName(district.governorateName))
  );
};

const governorateHasFacility = (gov, facility) => {
  return (
    gov.matchKeys.includes(normalize(facility.governorateKey)) ||
    gov.matchKeys.includes(normalizeName(facility.governorateName))
  );
};

const tableRows = computed(() => {
  return governorates.value.map((gov) => {
    const govDistricts = districts.value.filter((district) =>
      governorateHasDistrict(gov, district)
    );

    const govFacilities = facilities.value.filter((facility) =>
      governorateHasFacility(gov, facility)
    );

    const evaluatedDistricts = new Set(
      govFacilities
        .map((facility) => {
          return (
            normalize(facility.districtKey) ||
            normalizeName(facility.districtName)
          );
        })
        .filter(Boolean)
    );

    const districtsCount = govDistricts.length || evaluatedDistricts.size;
    const evaluated = evaluatedDistricts.size;
    const pending = Math.max(districtsCount - evaluated, 0);

    return {
      key: gov.key,
      name: gov.name,
      districts: districtsCount,
      evaluated,
      pending,
      facilities: govFacilities.length,
      status:
        evaluated > 0
          ? t("nationalScope.status.partial", { count: evaluated })
          : t("nationalScope.status.pending"),
      statusType: evaluated > 0 ? "partial" : "pending",
    };
  });
});

const filteredRows = computed(() => {
  return tableRows.value.filter((item) => {
    return (
      (!filters.governorate || item.key === filters.governorate) &&
      (!filters.status || item.statusType === filters.status)
    );
  });
});

const totalGovernorates = computed(() => governorates.value.length);
const totalDistricts = computed(() => districts.value.length);

const evaluatedDistrictsCount = computed(() => {
  const list = new Set(
    facilities.value
      .map(
        (item) =>
          normalize(item.districtKey) || normalizeName(item.districtName)
      )
      .filter(Boolean)
  );

  return list.size;
});

const pendingDistrictsCount = computed(() =>
  Math.max(totalDistricts.value - evaluatedDistrictsCount.value, 0)
);

const resetFilters = () => {
  filters.governorate = null;
  filters.status = null;
};

const loadData = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    await Promise.all([loadGovernorates(), loadDistricts(), loadFacilities()]);
  } catch (error) {
    errorMessage.value = t("nationalScope.loadError");
  } finally {
    loading.value = false;
  }
};

onMounted(loadData);
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800;900&display=swap");

.dashboard-page {
  background: #f4f6f9;
  min-height: 100vh;
  padding: 26px 22px 60px;
  color: #1a2433;
  font-family: "Cairo", Tahoma, sans-serif;
}

.section-head {
  text-align: center;
  margin-bottom: 22px;
}

.section-head h2 {
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 10px;
  color: #0f172a;
}

.section-head p {
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.9;
  font-size: 14px;
  color: #4c5b6e;
}

.loading-wrapper {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.93);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  backdrop-filter: blur(5px);
}

.loading-card {
  width: 320px;
  max-width: 90%;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 20px;
  padding: 34px 24px;
  text-align: center;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.loading-card h3 {
  margin-top: 18px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.loading-card p {
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

.kpi-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 12px;
  padding: 18px 16px;
  min-height: 125px;
  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
  border-right: 5px solid #c3cedb;
}

.kpi-card.blue {
  border-right-color: #173f73;
}

.kpi-card.green {
  border-right-color: #15803d;
}

.kpi-card.orange {
  border-right-color: #b45309;
}

.kpi-title {
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 12px;
}

.kpi-value {
  direction: ltr;
  text-align: right;
  font-size: 34px;
  font-weight: 800;
  color: #0f172a;
}

.kpi-note {
  font-size: 12px;
  color: #7a8799;
  margin-top: 8px;
}

.filter-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 160px;
  gap: 14px;
  align-items: start;
  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
}

.reset-btn {
  background: #b8923e !important;
  color: #111827 !important;
  border-radius: 9px !important;
  font-weight: 800;
}

.table-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
}

.table-card h3 {
  text-align: center;
  font-size: 18px;
  font-weight: 900;
  margin-bottom: 16px;
}

.table-wrap {
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
}

thead {
  background: #163d69;
}

th,
td {
  padding: 13px 14px;
  border-bottom: 1px solid #e5e7eb;
  text-align: right;
  font-size: 13px;
}

th {
  font-weight: 900;
  color: white;
}

td {
  color: #334155;
}

.status {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.status.partial {
  background: #fff7ed;
  color: #b45309;
  border: 1px solid #fed7aa;
}

.status.pending {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

.error-box,
.empty-row {
  text-align: center;
  padding: 25px;
  font-size: 14px;
  font-weight: 800;
  color: #64748b;
}

.error-box {
  color: #b91c1c;
  background: #fff1f1;
  border: 1px solid #fecaca;
  border-radius: 10px;
}

:deep(.v-field),
:deep(.v-btn),
:deep(.v-label),
:deep(.v-field__input),
:deep(.v-select__selection-text) {
  font-family: "Cairo", Tahoma, sans-serif !important;
}

:deep(.v-field) {
  border-radius: 9px !important;
  background: #fff;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-card {
    grid-template-columns: 1fr 1fr;
  }

  .reset-btn {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .dashboard-page {
    width: 100%;
    padding: 12px 8px 32px;
    overflow-x: hidden;
  }

  .section-head {
    margin-bottom: 16px;
    text-align: start;
  }

  .section-head h2 {
    margin-bottom: 8px;
    font-size: 18px;
    line-height: 1.6;
  }

  .section-head p {
    font-size: 12px;
    line-height: 1.8;
  }

  .kpi-grid,
  .filter-card {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }

  .kpi-card {
    min-height: auto;
    padding: 14px;
    border-radius: 12px;
  }

  .kpi-title {
    margin-bottom: 6px;
    font-size: 12px;
  }

  .kpi-value {
    font-size: 28px;
  }

  .kpi-note {
    margin-top: 4px;
    font-size: 11px;
  }

  .filter-card {
    padding: 12px;
    margin-bottom: 14px;
  }

  .reset-btn {
    width: 100%;
    min-height: 44px;
    grid-column: span 1;
  }

  .table-card {
    padding: 12px 8px;
    border-radius: 12px;
  }

  .table-card h3 {
    margin-bottom: 12px;
    font-size: 15px;
    line-height: 1.6;
  }

  .table-wrap {
    overflow-x: auto;
    border-radius: 10px;
    -webkit-overflow-scrolling: touch;
  }

  table {
    min-width: 760px;
  }

  th,
  td {
    padding: 11px 10px;
    font-size: 12px;
    white-space: nowrap;
  }

  .status {
    padding: 5px 9px;
    font-size: 11px;
  }

  .loading-card {
    width: calc(100% - 28px);
    padding: 26px 18px;
    border-radius: 16px;
  }

  :deep(.v-field__input) {
    min-height: 44px;
    font-size: 13px;
  }

  :deep(.v-label) {
    font-size: 13px;
  }
}
/* ================= Mobile table cards ================= */

@media (max-width: 768px) {
  .table-wrap {
    overflow: visible;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  table,
  thead,
  tbody,
  tr,
  th,
  td {
    display: block;
    width: 100%;
  }

  table {
    min-width: 0;
  }

  thead {
    display: none;
  }

  tbody {
    display: grid;
    gap: 12px;
  }

  tbody tr {
    position: relative;
    overflow: hidden;
    padding: 12px 14px;
    border: 1px solid #dde4ec;
    border-inline-start: 5px solid #173f73;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  }

  tbody tr:hover {
    background: #fff;
  }

  tbody td,
  tbody td:nth-child(1),
  tbody td:nth-child(2),
  tbody td:nth-child(3),
  tbody td:nth-child(4),
  tbody td:nth-child(5),
  tbody td:nth-child(6) {
    display: flex;
    width: 100%;
    min-width: 0;
    max-width: none;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px dashed #e2e8f0;
    font-size: 13px;
    line-height: 1.7;
    text-align: end;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
  }

  tbody td:last-child {
    border-bottom: 0;
  }

  tbody td::before {
    content: attr(data-label);
    display: block;
    flex: 0 0 42%;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.6;
    text-align: start;
    white-space: normal;
  }

  tbody td.governorate-name {
    display: block;
    padding: 2px 0 13px;
    color: #0f172a;
    font-size: 16px;
    font-weight: 900;
    line-height: 1.8;
    text-align: start;
    white-space: normal;
    overflow-wrap: break-word;
  }

  tbody td.governorate-name::before {
    display: none;
    content: none;
  }

  .status {
    max-width: 58%;
    text-align: center;
    white-space: normal;
  }

  .empty-card-row {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .empty-row {
    display: block;
    width: 100%;
    padding: 24px !important;
    border: 1px dashed #cbd5e1 !important;
    border-radius: 12px;
    background: #fff;
    text-align: center !important;
  }

  .empty-row::before {
    display: none;
    content: none;
  }
}

@media (max-width: 480px) {
  .dashboard-page {
    padding-inline: 6px;
  }

  .table-card {
    padding-inline: 6px;
  }

  tbody tr {
    padding: 11px 12px;
  }

  tbody td {
    gap: 10px;
    font-size: 12px;
  }

  tbody td::before {
    flex-basis: 44%;
    font-size: 10px;
  }

  tbody td.governorate-name {
    font-size: 15px;
  }

  .status {
    max-width: 56%;
    padding: 5px 8px;
    font-size: 10px;
  }
}

.dashboard-page[dir="ltr"] {
  text-align: left;
}

.dashboard-page[dir="ltr"] th,
.dashboard-page[dir="ltr"] td,
.dashboard-page[dir="ltr"] .kpi-value {
  text-align: left;
}

.dashboard-page[dir="ltr"] .kpi-card {
  border-right-width: 1px;
  border-left: 5px solid #c3cedb;
}

.dashboard-page[dir="ltr"] .kpi-card.blue {
  border-left-color: #173f73;
}

.dashboard-page[dir="ltr"] .kpi-card.green {
  border-left-color: #15803d;
}

.dashboard-page[dir="ltr"] .kpi-card.orange {
  border-left-color: #b45309;
}
</style>
